import React from 'react';

import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

import { useUserInfo } from '@src/hooks/userInfo';
import { collectStates } from '@src/utils/getStatesFromProducts';

import { AlertCategory } from './AlertCategory';
import { Categories } from './Categories';
import { Products } from './Products';
import { Publication } from './Publication';
import { ReadTime } from './ReadTime';
import { SalesChannels } from './SalesChannels';
import { States } from './States';

const ContentInfoHeader = ({
    alertCategory,
    categories,
    products,
    platformProducts,
    publishedAt,
    readTime = null,
    roleType,
    salesChannels,
    states,
    webinar,
    updatedAt,
    effectiveUntilMsg,
    agencyName,
}) => {
    const userInfo = useUserInfo();
    const renderAlertCategory = () => {
        if (!alertCategory) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/list-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">Category:</span>
                { ' ' }
                <AlertCategory alertCategory={alertCategory} />
            </span>
        );
    };

    const renderCategories = () => {
        if (!categories || categories.length === 0) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/list-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">Categories:</span>
                { ' ' }
                <Categories categories={categories} />
            </span>
        );
    };

    const renderManagerOnly = () => {
        if (!roleType || roleType !== 'manager') {
            return null;
        }

        return <span>Manager only</span>;
    };

    const renderProducts = () => {
        if (!platformProducts || platformProducts.length === 0) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/tag-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">Products:</span>
                { ' ' }
                <Products products={platformProducts} />
            </span>
        );
    };

    const renderPublished = () => {
        if (!updatedAt) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/calendar-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">Date:</span>
                { ' ' }
                <Publication
                    alertCategory={alertCategory}
                    publishedAt={publishedAt}
                    readTime={readTime}
                    webinar={webinar}
                    updatedAt={updatedAt}
                />
                &nbsp;&nbsp;&nbsp;&nbsp;
            </span>
        );
    };

    const renderEffectiveUntilMsg = () => {
        if (!effectiveUntilMsg) {
            return null;
        }

        return effectiveUntilMsg;
    };

    const renderReadTime = () => {
        if (!readTime) {
            return null;
        }

        return <ReadTime readTime={readTime} />;
    };

    const renderSalesChannels = () => {
        if (userInfo.accessLevel !== 'admin') {
            return null;
        }

        if (!salesChannels || salesChannels.length === 0) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/tag-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">Sales Channels:</span>
                { ' ' }
                <SalesChannels salesChannels={salesChannels} />
            </span>
        );
    };

    const renderStates = () => {
        let allStates = collectStates(states, products);

        if (!products || products.length === 0) {
            allStates = collectStates(states, platformProducts);
        }

        if (!allStates || allStates.length === 0) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/pin-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">States:</span>
                { ' ' }
                <States states={allStates} />
            </span>
        );
    };

    const renderAgencyName = () => {
        if (!agencyName) {
            return null;
        }

        return (
            <span className="info-header">
                <img
                    src="/img/icons/agency-icon.svg"
                    height="18"
                    alt=""
                />
                <span className="title">Agency Name:</span>
                { ' ' }
                <span>{agencyName}</span>
            </span>
        );
    };

    return (
        <div className="post_subtitle">
            <Row className="pb-3">
                <Col>
                    { renderEffectiveUntilMsg() }
                </Col>
            </Row>

            <Row>
                <Col>
                    { renderPublished() }
                    { renderAlertCategory() }
                    { renderCategories() }
                    { renderAgencyName() }
                </Col>
            </Row>

            <Row>
                <Col>
                    { renderProducts() }
                </Col>
            </Row>

            <Row>
                <Col>
                    { renderSalesChannels() }
                </Col>
            </Row>

            <Row>
                <Col>
                    { renderReadTime() }
                </Col>
            </Row>

            <Row>
                <Col>
                    { renderManagerOnly()}
                </Col>
            </Row>

            <Row>
                <Col>
                    { renderStates() }
                </Col>
            </Row>
        </div>
    );
};

ContentInfoHeader.propTypes = {
    alertCategory: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
    })),
    platformProducts: PropTypes.arrayOf(PropTypes.object),
    publishedAt: PropTypes.string,
    readTime: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    roleType: PropTypes.string,
    salesChannels: PropTypes.arrayOf(PropTypes.object),
    states: PropTypes.arrayOf(PropTypes.object),
    webinar: PropTypes.bool,
    updatedAt: PropTypes.string,
    effectiveUntilMsg: PropTypes.node,
    agencyName: PropTypes.string,
};


export default ContentInfoHeader;
